import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Play, Pause, Loader2 } from "lucide-react";
import { BACKEND_URL } from "../lib/api";
import { useAudioPlayer } from "../lib/audioPlayer";
import ShareNote from "../components/ShareNote";
import NewsletterForm from "../components/NewsletterForm";
import JsonLd from "../components/JsonLd";
import PremiumBackdrop from "../components/PremiumBackdrop";
import { usePageTitle } from "../lib/usePageTitle";

const API = `${BACKEND_URL}/api`;

function buildArticleSchema(note) {
  if (!note) return null;
  const url = `https://getvirtualgrowth.com/field-notes/${note.slug}`;
  const bodyText = (note.body || [])
    .map((b) => (b && b.text ? b.text : ""))
    .filter(Boolean)
    .join("\n\n");
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${url}#article`,
    headline: note.title,
    description: note.subtitle || "A Sunday Field Note from Krishna S.",
    articleBody: bodyText,
    inLanguage: "en",
    url,
    mainEntityOfPage: url,
    datePublished: note.published_at,
    dateModified: note.published_at,
    wordCount: bodyText.split(/\s+/).filter(Boolean).length,
    author: {
      "@type": "Person",
      "@id": "https://getvirtualgrowth.com/#krishna",
      name: "Krishna S.",
      url: "https://getvirtualgrowth.com/",
    },
    publisher: {
      "@type": "Organization",
      name: "Get Virtual Growth",
      url: "https://getvirtualgrowth.com/",
      logo: {
        "@type": "ImageObject",
        url: "https://getvirtualgrowth.com/icon-512.png",
      },
    },
    image: "https://getvirtualgrowth.com/og-cover.png",
    isAccessibleForFree: true,
    keywords: [
      "field notes",
      "small business",
      "growth partner",
      "technology partner",
      "founder letter",
      note.kicker || "",
    ].filter(Boolean).join(", "),
  };
}

function buildBreadcrumbSchema(note) {
  if (!note) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://getvirtualgrowth.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Field Notes",
        item: "https://getvirtualgrowth.com/field-notes",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: note.title,
        item: `https://getvirtualgrowth.com/field-notes/${note.slug}`,
      },
    ],
  };
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

/** Thin brass progress bar at the top of the screen, grows as you scroll. */
function ReadingProgress() {
  const { scrollYProgress } = useScroll();
  const width = useSpring(scrollYProgress, { stiffness: 90, damping: 20, restDelta: 0.001 });
  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-[2px] bg-brass origin-left z-40"
      style={{ scaleX: width }}
      aria-hidden="true"
    />
  );
}

/** Compose the narration text — only used for length gating; global player composes independently. */
function narrationLength(note) {
  if (!note) return 0;
  let total = (note.title || "").length + (note.subtitle || "").length;
  (note.body || []).forEach((b) => {
    if (b && b.text) total += b.text.length + 1;
  });
  return total;
}

function NoteAudioPlayer({ note }) {
  const { current, status, play } = useAudioPlayer();
  const len = narrationLength(note);

  if (!len || len > 9000) return null;

  const isThisNote = current?.slug === note.slug;
  const isPlaying = isThisNote && status === "playing";
  const isLoading = isThisNote && status === "loading";

  let iconEl;
  if (isLoading) iconEl = <Loader2 className="h-4 w-4 animate-spin" />;
  else if (isPlaying) iconEl = <Pause className="h-4 w-4" />;
  else iconEl = <Play className="h-4 w-4 translate-x-[1px]" />;

  const label = isThisNote
    ? isPlaying
      ? "Playing. Take it with you across the site."
      : isLoading
      ? "Loading narration..."
      : "Paused. Press play to keep going."
    : "Prefer to close your eyes and just listen? Press play.";

  const kicker = isThisNote && status === "error"
    ? "Something went wrong, tap to retry"
    : "Listen in Krishna's voice";

  return (
    <button
      type="button"
      onClick={() => play(note)}
      className="group relative mt-10 inline-flex items-center gap-4 rounded-full pl-3 pr-6 py-3 backdrop-blur-md overflow-hidden gvg-listen-pulse"
      style={{
        background: "rgba(0, 0, 0, 0.5)",
        border: "1px solid rgba(201, 169, 97, 0.4)",
        transition:
          "border-color 500ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 500ms cubic-bezier(0.2, 0.8, 0.2, 1)",
      }}
      data-testid="field-note-audio-player"
      aria-label={isPlaying ? "Pause narration" : "Play narration"}
    >
      {/* Soft champagne bloom on hover */}
      <span
        aria-hidden
        className="absolute -inset-4 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(201,169,97,0.22), transparent 65%)",
          filter: "blur(18px)",
        }}
      />
      {/* Top-edge highlight */}
      <span
        aria-hidden
        className="absolute inset-x-6 top-0 h-px opacity-70"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(201,169,97,0.55), transparent)",
        }}
      />
      <span
        className="relative flex h-9 w-9 items-center justify-center rounded-full border border-brass/70 text-brass group-hover:text-bg group-hover:bg-brass transition-colors duration-500"
        data-testid="field-note-audio-toggle"
      >
        {iconEl}
      </span>
      <span className="relative flex flex-col items-start leading-tight text-left min-w-0">
        <span className="font-sans text-[10px] uppercase tracking-mega text-brass whitespace-nowrap">
          {kicker}
        </span>
        <span className="mt-1 font-serif italic text-ink text-[15px] truncate max-w-[280px] sm:max-w-none">
          {label}
        </span>
      </span>
    </button>
  );
}

export default function FieldNote() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "1";
  const [note, setNote] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(null);
  usePageTitle(note?.title ? note.title : "Field Note");

  // Per-note OG / Twitter injection once the note resolves.
  useEffect(() => {
    if (!note) return;
    const desc = note.subtitle || note.kicker || "A Sunday Field Note from Krishna S.";
    const upsert = (sel, tag, attrs) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement(tag); document.head.appendChild(el); }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    };
    upsert('meta[name="description"]', "meta", { name: "description", content: desc });
    upsert('meta[property="og:title"]', "meta", { property: "og:title", content: note.title });
    upsert('meta[property="og:description"]', "meta", { property: "og:description", content: desc });
    upsert('meta[property="og:type"]', "meta", { property: "og:type", content: "article" });
    upsert('meta[name="twitter:title"]', "meta", { name: "twitter:title", content: note.title });
    upsert('meta[name="twitter:description"]', "meta", { name: "twitter:description", content: desc });
  }, [note]);

  useEffect(() => {
    setNote(null);
    setRelated([]);
    setError(null);
    window.scrollTo(0, 0);

    // Draft preview mode: hit the admin endpoint with the token stored in
    // localStorage (set when the founder logged into /admin). Falls back to
    // an "unauthorized preview" error if the token is missing or invalid.
    if (isPreview) {
      const adminToken = localStorage.getItem("gvg_admin_token");
      if (!adminToken) {
        setError("Preview mode needs an admin session. Sign in at /admin first, then re-open this preview link.");
        return;
      }
      axios
        .get(`${API}/admin/field-notes/${slug}`, {
          headers: { "X-Admin-Token": adminToken },
        })
        .then((r) => setNote(r.data))
        .catch(() => setError("Preview failed. Your admin session may have expired - re-log at /admin."));
      return;
    }

    axios
      .get(`${API}/field-notes/${slug}`)
      .then((r) => setNote(r.data))
      .catch(() => setError("This field note could not be found."));
    axios
      .get(`${API}/field-notes`)
      .then((r) => setRelated((r.data || []).filter((n) => n.slug !== slug).slice(0, 3)))
      .catch(() => {});
  }, [slug, isPreview]);

  if (error) {
    return (
      <main className="pt-40 pb-24 max-w-3xl mx-auto px-6 md:px-12" data-testid="field-note-not-found">
        <p className="font-serif italic text-2xl text-ink-muted">{error}</p>
        <Link to="/field-notes" className="mt-8 inline-flex items-center gap-2 font-sans text-xs uppercase tracking-mega text-forest hover:gap-3 transition-all">
          <ArrowLeft className="h-4 w-4" /> Back to all field notes
        </Link>
      </main>
    );
  }

  if (!note) {
    return (
      <main className="pt-40 pb-24 max-w-3xl mx-auto px-6 md:px-12">
        <p className="font-sans text-sm text-ink-muted">Loading&hellip;</p>
      </main>
    );
  }

  return (
    <main className="bg-bg pt-28 md:pt-36 pb-28 relative overflow-hidden gvg-typography" data-testid="field-note-page">
      {isPreview && (
        <div
          className="fixed top-0 inset-x-0 z-40 text-center py-2 text-[11px] uppercase tracking-mega"
          style={{
            background: "rgba(201,169,97,0.16)",
            color: "#C9A961",
            borderBottom: "1px solid rgba(201,169,97,0.35)",
            backdropFilter: "blur(8px)",
          }}
          data-testid="field-note-preview-banner"
        >
          Draft preview {note.is_published === false ? " - not yet published" : ""} - visible only with an admin session
        </div>
      )}
      <PremiumBackdrop />
      <JsonLd id={`article-${note.slug}`} data={buildArticleSchema(note)} />
      <JsonLd id={`breadcrumb-${note.slug}`} data={buildBreadcrumbSchema(note)} />
      <ReadingProgress />
      <article className="max-w-2xl mx-auto px-6 md:px-8">
        <Link to="/field-notes" className="inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-mega text-ink-muted hover:text-forest transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> All field notes
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8"
        >
          <div className="flex items-center gap-3 text-brass">
            <span className="h-px w-8 bg-brass/60" />
            <p className="font-sans text-[11px] uppercase tracking-mega">{note.kicker || "A field note"}</p>
          </div>
          <h1 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.08] text-ink">{note.title}</h1>
          {note.subtitle && (
            <p className="mt-6 font-serif italic text-xl md:text-[22px] text-ink-muted leading-relaxed">{note.subtitle}</p>
          )}
          <p className="mt-8 font-sans text-[11px] uppercase tracking-mega text-ink-muted">
            {formatDate(note.published_at)} &middot; {note.read_minutes || 2} min read &middot; by Krishna S
          </p>
          <NoteAudioPlayer note={note} />
          {note.slug === "the-founder-who-didnt-notice" && (
            <div
              className="mt-8 rounded-2xl overflow-hidden mx-auto"
              data-testid="field-note-video"
              style={{
                maxWidth: 520,
                background: "linear-gradient(180deg, #14141A 0%, #0A0A0A 100%)",
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow: "0 30px 80px -30px rgba(201,169,97,0.2)",
                aspectRatio: "720 / 940",
              }}
            >
              <video
                controls
                playsInline
                preload="metadata"
                poster="/video/krishna-founder-story-poster.jpg"
                className="w-full h-full object-cover"
              >
                <source src="/founder/krishna-founder-story.mp4" type="video/mp4" />
              </video>
            </div>
          )}
          {note.slug === "the-founder-who-didnt-notice" && (
            <p className="mt-3 font-sans text-[11px] uppercase tracking-mega text-brass text-center">
              Watch Krishna tell this story · 2 min 51 sec
            </p>
          )}
          <div className="mt-6">
            <ShareNote
              title={note.title}
              text={note.subtitle || note.kicker || ""}
              url={typeof window !== "undefined" ? window.location.href : ""}
            />
          </div>
        </motion.header>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.25 }}
          className="mt-14 book-reading"
          data-testid="field-note-body"
        >
          {(note.body || []).map((b, i) => {
            if (b.type === "quote") {
              return (
                <blockquote key={i}>
                  &ldquo;{b.text}&rdquo;
                </blockquote>
              );
            }
            return <p key={i}>{b.text}</p>;
          })}
        </motion.div>

        {/* signoff */}
        <div className="mt-14 flex items-center gap-4">
          <img
            src="/founder/krishna-portrait-2026.jpg?v=3"
            alt="Krishna S"
            className="h-14 w-14 rounded-full object-cover ring-1 ring-line"
            data-testid="founder-avatar-fieldnote"
          />
          <div>
            <p className="font-serif text-lg text-ink">Krishna S</p>
            <p className="font-sans text-[11px] uppercase tracking-mega text-ink-muted">Founder &middot; Get Virtual Growth</p>
          </div>
        </div>

        {/* Share prompt removed per founder request */}

        {/* CTA */}
        <div className="mt-10 rounded-3xl bg-forest text-bg p-10 md:p-12">
          <p className="font-sans text-[11px] uppercase tracking-mega text-brass">If this landed with you</p>
          <h3 className="mt-4 font-serif text-3xl md:text-4xl leading-snug max-w-xl">
            Let me look at your business and tell you where your pipe is leaking.
          </h3>
          <Link
            to="/"
            data-testid="field-note-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-bg px-6 py-3 font-sans text-xs uppercase tracking-wider font-semibold text-forest hover:bg-white transition-colors"
          >
            Start with a diagnosis
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-16">
          <Link to="/field-notes" className="inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-mega text-ink-muted hover:text-forest transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Read more field notes
          </Link>
        </div>
      </article>

      {/* Recommended next note — hero card + supporting */}
      {related.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 md:px-8 mt-24" data-testid="field-note-related">
          <div className="flex items-center gap-3 text-brass mb-8">
            <span className="h-px w-8 bg-brass/60" />
            <p className="font-sans text-[11px] uppercase tracking-mega">Read this next</p>
          </div>

          {/* Hero recommended card */}
          <Link
            to={`/field-notes/${related[0].slug}`}
            data-testid={`recommended-next-${related[0].slug}`}
            className="group block rounded-3xl border border-line bg-surface p-8 md:p-12 hover:border-forest/40 transition-colors"
          >
            <div className="flex items-center justify-between gap-6">
              <p className="font-sans text-[10px] uppercase tracking-mega text-brass">{related[0].kicker || "A field note"}</p>
              <span className="inline-flex items-center gap-1.5 font-sans text-[10px] uppercase tracking-mega text-forest opacity-0 group-hover:opacity-100 transition-opacity">
                Open <ArrowUpRight className="h-3 w-3" />
              </span>
            </div>
            <h3 className="mt-4 font-serif text-3xl md:text-4xl text-ink group-hover:text-forest transition-colors leading-[1.15] max-w-3xl">
              {related[0].title}
            </h3>
            {related[0].subtitle && (
              <p className="mt-4 font-serif italic text-lg text-ink-muted leading-relaxed max-w-2xl">
                {related[0].subtitle}
              </p>
            )}
            <p className="mt-6 font-sans text-[11px] uppercase tracking-mega text-ink-muted">
              {formatDate(related[0].published_at)} &middot; {related[0].read_minutes || 2} min read
            </p>
          </Link>

          {/* Supporting picks */}
          {related.length > 1 && (
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              {related.slice(1).map((r) => (
                <Link
                  key={r.slug}
                  to={`/field-notes/${r.slug}`}
                  data-testid={`related-note-${r.slug}`}
                  className="group block rounded-2xl border border-line bg-surface p-6 hover:border-forest/40 transition-colors"
                >
                  <p className="font-sans text-[10px] uppercase tracking-mega text-brass">{r.kicker || "A field note"}</p>
                  <h3 className="mt-3 font-serif text-2xl text-ink group-hover:text-forest transition-colors leading-snug">
                    {r.title}
                  </h3>
                  <p className="mt-4 font-sans text-[11px] uppercase tracking-mega text-ink-muted">
                    {formatDate(r.published_at)} &middot; {r.read_minutes || 2} min
                  </p>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Newsletter subscribe */}
      <section className="max-w-3xl mx-auto px-6 md:px-8 mt-20">
        <NewsletterForm source={`field-note-${note.slug}`} />
      </section>
    </main>
  );
}
